import * as THREE from 'three'
import { times } from '@/utils'
import { randBool, randFloat, randVector3 } from '@/utils/random'
import { inRange } from '@/utils/math'
import Bird from './Bird'

// See: http://www.kfish.org/boids/pseudocode.html

/* eslint-disable */
const defaultSettings = {
  width: 500,      // width of release area (x)
  height: 250,     // height of release area (y)
  depth: 500,      // depth (or length) of release area (z)
  count: 100,
  size: 1,
  color: 0x000000,
  cohesion: 1.0,
  separation: 1.0,
  alignment: 1.0
}
/* eslint-enable */

const MAX_SPEED = 1.0
const MAX_FORCE = 0.05

const limit = (v, max) => {
  const s = v.length()
  if (s > max) v.divideScalar(s / max)
}

export default class Flock extends THREE.Group {
  name = 'Flock'
  birds = []

  constructor (settings) {
    super()

    this.debug = process.env.NODE_ENV !== 'production'
    this.settings = Object.assign({}, defaultSettings, settings)

    this.createBirds()
    this.debug && this.drawBoundingBox()
  }

  createBirds () {
    const { width, height, depth, count, size, color } = this.settings

    times(count, () => {
      const bird = new Bird(color)
      bird.position.copy(randVector3(width, height, depth))
      bird.velocity.copy(randVector3(1, 1, 1))
      bird.rotation.set(0, randFloat(2 * Math.PI), 0)
      bird.phase = Math.random()
      bird.size = size
      this.birds.push(bird)
      this.add(bird)
    })
  }

  drawBoundingBox () {
    const { width, height, depth, color } = this.settings
    const geometry = new THREE.BoxGeometry(width, height, depth)
    const material = new THREE.MeshBasicMaterial({ color, wireframe: true })
    const bbox = new THREE.Mesh(geometry, material)
    bbox.name = 'debug'
    this.add(bbox)
  }

  avoidBounds (bird) {
    const bound = new THREE.Vector3()
    let distance

    const avoid = (x, y, z) => {
      bound.set(x, y, z)
      distance = bird.position.distanceToSquared(bound)
      const force = bird.position.clone()
        .sub(bound)
        .multiplyScalar(5 / distance)
      bird.velocity.add(force)
    }

    const { width, height, depth } = this.settings
    const { x, y, z } = bird.position

    avoid(-width / 2, y, z)
    avoid(width / 2, y, z)
    avoid(x, -height / 2, z)
    avoid(x, height / 2, z)
    avoid(x, y, -depth / 2)
    avoid(x, y, depth / 2)
  }

  // separation
  keepDistance (bird) {
    const range = this.settings.separation * 100
    const force = new THREE.Vector3()
    let distance

    this.birds.forEach(other => {
      distance = bird.position.distanceTo(other.position)
      if (inRange(distance, 0, range) && randBool()) {
        force.subVectors(bird.position, other.position)
          .normalize()
          .divideScalar(distance)
        bird.velocity.add(force)
      }
    })
  }

  // alignment
  matchDirection (bird) {
    const range = this.settings.alignment * 100
    const force = new THREE.Vector3()
    let count = 0

    this.birds.forEach(other => {
      const distance = bird.position.distanceTo(other.position)
      if (inRange(distance, 0, range) && randBool()) {
        force.add(other.velocity)
        count += 1
      }
    })

    if (count > 0) {
      force.divideScalar(count)
      limit(force, MAX_FORCE)
      bird.velocity.add(force)
    }
  }

  // cohesion
  goToCenter (bird) {
    const range = this.settings.cohesion * 100
    const force = new THREE.Vector3()
    let count = 0

    this.birds.forEach(other => {
      const distance = bird.position.distanceTo(other.position)
      if (inRange(distance, 0, range) && randBool()) {
        force.add(other.position)
        count += 1
      }
    })

    if (count > 0) {
      force.divideScalar(count).sub(bird.position)
      limit(force, MAX_FORCE)
      bird.velocity.add(force)
    }
  }

  applyChanges (bird) {
    limit(bird.velocity, MAX_SPEED)

    bird.position.add(bird.velocity)

    bird.rotation.y = Math.atan2(-bird.velocity.z, bird.velocity.x)
    bird.rotation.z = Math.asin(bird.velocity.y / bird.velocity.length())

    bird.phase += Math.max(0.1, bird.velocity.y)
  }

  update () {
    const flock = randBool(0.3)

    this.birds.forEach(bird => {
      this.avoidBounds(bird)

      if (flock) {
        this.keepDistance(bird)
        this.matchDirection(bird)
        this.goToCenter(bird)
      }

      this.applyChanges(bird)
    })
  }
}
